import VirtualizedList from '@components/VirtualizedList.tsx'
import { useIsWideLayout } from '@hooks/useMediaQuery'
import useNavigator from '@hooks/useNavigator'
import useTypedTranslation from '@hooks/useTypedTranslation'
import { useTodoListContext } from '@routes/TodoList/Context'
import FilterSection from '@routes/TodoList/FilterSection'
import NoResults from '@routes/TodoList/NoResults'
import ProductCardTodoList from '@routes/TodoList/ProductCardTodoList'
import { type TransformedTodoListResponse, useCompleteTask, useReopenTask, useTodoCount } from '@state/todoList.ts'

import TasksDone from './assets/tasksDone.svg?no-inline'
import TasksZero from './assets/tasksZero.svg?no-inline'

import type { FilterAndSort, TodoItemWrapper } from '@state/util/todoList'

function SearchBar() {
  const isWideLayout = useIsWideLayout()
  const { goToStoreMap } = useNavigator()
  const { mutate: completeTask } = useCompleteTask()
  const { mutate: reopenTask } = useReopenTask()

  const { filteredTodoList, filterAndSort, selectedTask, setSelectedTask, selectedShelfId, setSelectedShelfId } = useTodoListContext()

  function handleItemSelect(item: TodoItemWrapper) {
    if (isWideLayout) {
      setSelectedTask(item)
    } else {
      goToStoreMap(item.product.gtin, true)
    }
  }

  async function handleItemComplete(item: TodoItemWrapper) {
    completeTask({ taskId: item.id })

    // if the completed item was associated with the selected shelf, clear the selection
    if (item.shelfId === selectedShelfId) {
      setSelectedShelfId(undefined)
    }

    const indexOfTask = filteredTodoList.findIndex(i => i.id === item.id)
    if (indexOfTask === filteredTodoList.length - 1) return
    const nextTask = filteredTodoList[indexOfTask + 1]
    setSelectedTask(nextTask)
  }

  async function handleItemUndo(item: TodoItemWrapper) {
    reopenTask({ taskId: item.id })
    setSelectedTask(undefined)
  }

  return (
    <>
      <FilterSection />

      <div className='relative flex-1'>
        <div className='absolute inset-0 overflow-hidden'>
          <TodoList
            todoItems={filteredTodoList}
            selectedTask={selectedTask}
            filterAndSort={filterAndSort}
            onSelect={handleItemSelect}
            onComplete={handleItemComplete}
            onUndo={handleItemUndo}
          />
        </div>
      </div>
    </>
  )
}

interface TodoListProps {
  todoItems: TransformedTodoListResponse
  selectedTask: TodoItemWrapper | undefined
  filterAndSort: FilterAndSort
  onSelect: (task: TodoItemWrapper) => void
  onComplete: (task: TodoItemWrapper) => void
  onUndo: (task: TodoItemWrapper) => void
}

function TodoList({ todoItems, onSelect, onComplete, onUndo, selectedTask, filterAndSort }: TodoListProps) {
  const { t } = useTypedTranslation()
  const { countCompleted, countOpen } = useTodoCount()

  if (filterAndSort.completed && countCompleted === 0) {
    return <NoResults message={t('todoList.emptyStates.noCompletedToday')} illustration={TasksZero} />
  }

  if (!filterAndSort.completed && countOpen === 0) {
    return <NoResults message={t('todoList.emptyStates.allTasksDoneToday')} illustration={TasksDone} />
  }

  if (todoItems.length === 0) {
    return (
      <NoResults
        message={t('todoList.emptyStates.allTasksDoneInStore', { category: t(`todoList.badges.${filterAndSort.primaryFilter}`) })}
        illustration={TasksDone}
      />
    )
  }

  return (
    <VirtualizedList
      items={todoItems}
      renderItem={item => {
        return (
          <div className='mb-2'>
            <ProductCardTodoList
              item={item}
              isSelected={selectedTask?.id === item.id}
              onSelect={() => onSelect(item)}
              onComplete={() => onComplete(item)}
              onUndo={() => onUndo(item)}
            />
          </div>
        )
      }}
    />
  )
}

export default SearchBar